import type {
  PoiCategory,
  PoiCategoryConfig,
  PoiClusterConfig,
  PoiGroup,
  PoiPopupContext,
} from './types';

// ─── Tooltips (hover) ───────────────────────────────────────────────────────
function basicTip(props: Record<string, unknown>): string {
  const name = typeof props.name === 'string' ? props.name : '—';
  return `<strong>${escapeHtml(name)}</strong>`;
}

function weatherTip(props: Record<string, unknown>): string {
  const name = typeof props.name === 'string' ? props.name : '—';
  const alt = props.alt_m != null ? `${props.alt_m} м` : '';
  const tMean = props.t_mean_c != null ? `${props.t_mean_c}°C` : '';
  const source = typeof props.source === 'string' ? props.source : '';
  return `
    <strong>${escapeHtml(name)}</strong><br/>
    ${alt ? `<span style="color:#666">Высота: ${alt}</span><br/>` : ''}
    ${tMean ? `<span style="color:#666">T° ср.: ${tMean}</span><br/>` : ''}
    ${source ? `<span style="color:#999;font-size:11px">${escapeHtml(source)}</span>` : ''}
  `.trim();
}

// ─── Popup helpers ──────────────────────────────────────────────────────────
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function osmLink(props: Record<string, unknown>): string | null {
  const id = props.osm_id;
  if (id == null) return null;
  const t = typeof props.osm_type === 'string' ? props.osm_type : 'node';
  const kind = t === 'way' || t === 'relation' ? t : 'node';
  return `https://www.openstreetmap.org/${kind}/${encodeURIComponent(String(id))}`;
}

function googleMapsLink(name: string, lat: number, lon: number): string {
  const q = name && name !== '—' ? `${name} ${lat},${lon}` : `${lat},${lon}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function googleSearchLink(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

function wikipediaLink(tag: string): string | null {
  // OSM формат: "lang:Page Title" (напр. "it:Stazione di Trento").
  const m = /^([a-z]{2,3}):(.+)$/.exec(tag.trim());
  if (!m) return null;
  const [, lang, title] = m;
  return `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`;
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function readStr(props: Record<string, unknown>, key: string): string | null {
  const v = props[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

interface ActionLink {
  href: string;
  label: string;
  emoji: string;
}

interface MetaRow {
  emoji: string;
  text: string;
}

function renderPopup(
  name: string,
  nameHref: string,
  emoji: string,
  categoryLabel: string,
  meta: MetaRow[],
  actions: ActionLink[],
): string {
  const links = actions
    .map(
      (a) =>
        `<a class="poi-popup__action" href="${a.href}" target="_blank" rel="noopener noreferrer"><span class="poi-popup__action-icon">${a.emoji}</span>${escapeHtml(a.label)}</a>`,
    )
    .join('');
  const metaRows = meta
    .map(
      (m) =>
        `<div class="poi-popup__meta-row"><span class="poi-popup__action-icon">${m.emoji}</span><span>${escapeHtml(m.text)}</span></div>`,
    )
    .join('');
  return `
    <div class="poi-popup">
      <a class="poi-popup__name" href="${nameHref}" target="_blank" rel="noopener noreferrer">${escapeHtml(name)}</a>
      <p class="poi-popup__cat"><span aria-hidden="true">${emoji}</span> ${escapeHtml(categoryLabel)}</p>
      ${metaRows ? `<div class="poi-popup__meta">${metaRows}</div>` : ''}
      <div class="poi-popup__actions">${links}</div>
    </div>
  `.trim();
}

function getName(props: Record<string, unknown>): string {
  if (typeof props.name === 'string' && props.name.trim()) return props.name;
  return '—';
}

// ─── Popup formatters (click) ───────────────────────────────────────────────
//
// Принцип JTBD-ориентированный: каждый POI отвечает на главный вопрос
// пользователя 3-4 строками meta-данных. Адрес/телефон/часы общие; всё
// остальное per category. Источник — реальные OSM-теги, google fallback
// только если нет website.
//
// Bilingual translation dictionaries for OSM values. `[en, ru]` tuples — kept
// inline rather than going through i18next because these strings only appear
// inside imperatively-rendered Leaflet popup HTML and we already pass `lang`
// down explicitly. Extracting to a JSON dictionary would not pay off here.
type Lang = 'en' | 'ru';
type Pair = [en: string, ru: string];

const pickLang = (pair: Pair | undefined, lang: Lang, fallback: string): string => {
  if (!pair) return fallback;
  return lang === 'ru' ? pair[1] : pair[0];
};

const AERIALWAY_LABELS: Record<string, Pair> = {
  gondola: ['gondola', 'кабинка'],
  chair_lift: ['chair lift', 'кресельный'],
  drag_lift: ['drag lift', 'бугельный'],
  cable_car: ['cable car', 'фуникулёр'],
  mixed_lift: ['mixed lift', 'комбинированный'],
  magic_carpet: ['magic carpet', 'ленточный'],
  platter: ['platter', 'тарелочка'],
  rope_tow: ['rope tow', 'буксир'],
  't-bar': ['T-bar', 'Т-бар'],
  'j-bar': ['J-bar', 'J-бар'],
};

const ISCED_LABELS: Record<string, Pair> = {
  '0': ['preschool', 'дошкольное'],
  '1': ['primary (1-5)', 'начальная (1-5)'],
  '2': ['middle (6-8)', 'средняя (6-8)'],
  '3': ['high (9-13)', 'лицей (9-13)'],
  '4': ['post-secondary', 'постсреднее'],
  '5': ['university', 'университет'],
  '6': ['university', 'университет'],
  '7': ['master / PhD', 'магистратура'],
};

const OPERATOR_TYPE_LABELS: Record<string, Pair> = {
  public: ['public (government)', 'государственная'],
  private: ['private', 'частная'],
  religious: ['religious', 'религиозная'],
  community: ['community', 'общественная'],
  ngo: ['NGO', 'НКО'],
};

const RAILWAY_LABELS: Record<string, Pair> = {
  station: ['main station', 'главная станция'],
  halt: ['halt', 'полустанок'],
  tram_stop: ['tram stop', 'трамвай'],
};

const AERODROME_LABELS: Record<string, Pair> = {
  international: ['international', 'международный'],
  regional: ['regional', 'региональный'],
  public: ['public', 'общественный'],
  private: ['private', 'частный'],
  military: ['military', 'военный'],
};

// Inline phrase translations used by buildMeta(). Same Pair shape.
const PHRASES = {
  altitude_m: (m: number, lang: Lang): string => (lang === 'ru' ? `${m} м` : `${m} m`),
  emergency_24_7: ['24/7 emergency room', 'есть скорая (24/7)'] as Pair,
  beds: (n: number, lang: Lang): string => (lang === 'ru' ? `${n} коек` : `${n} beds`),
  dispenses_rx: ['fills prescriptions', 'отпускают по рецепту'] as Pair,
  hypermarket: ['hypermarket', 'гипермаркет'] as Pair,
  age_range: (mi: number | string, ma: number | string, lang: Lang): string =>
    lang === 'ru' ? `возраст ${mi}–${ma} лет` : `age ${mi}–${ma}`,
  lifts: (n: number, lang: Lang): string => {
    if (lang === 'ru') return `${n} подъёмник${pluralRu(n)}`;
    return `${n} ${n === 1 ? 'lift' : 'lifts'}`;
  },
  beds_total: (n: number, lang: Lang): string =>
    lang === 'ru' ? `${n} мест` : `${n} sleeping places`,
  seasonal: ['seasonal', 'сезонный'] as Pair,
  year_round: ['open year-round', 'круглый год'] as Pair,
  shower: ['shower', 'душ'] as Pair,
  meals: ['meals', 'питание'] as Pair,
  wifi: ['Wi-Fi', 'Wi-Fi'] as Pair,
  length_m: (m: number, lang: Lang): string => (lang === 'ru' ? `${m} м длина` : `${m} m length`),
  km2: (n: number, lang: Lang): string => (lang === 'ru' ? `${n} км²` : `${n} km²`),
  km_from: (km: number, city: string, lang: Lang): string =>
    km < 1
      ? lang === 'ru' ? `в ${city}` : `in ${city}`
      : lang === 'ru' ? `${km} км от ${city}` : `${km} km from ${city}`,
};

function readNum(props: Record<string, unknown>, key: string): number | null {
  const v = props[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Общие meta: телефон, адрес, часы. Используются перед per-category блоком.
function commonMeta(props: Record<string, unknown>): MetaRow[] {
  const meta: MetaRow[] = [];
  const hours = readStr(props, 'opening_hours');
  if (hours) meta.push({ emoji: '🕐', text: hours });
  const phone = readStr(props, 'phone');
  if (phone) meta.push({ emoji: '☎', text: phone });
  return meta;
}

function nearestCityMeta(props: Record<string, unknown>): MetaRow | null {
  const city = readStr(props, 'nearest_city');
  const km = readNum(props, 'nearest_city_km');
  if (!city) return null;
  const text = km != null ? `${km < 1 ? 'в' : `${km} км от`} ${city}` : city;
  return { emoji: '📍', text };
}

function buildMeta(
  props: Record<string, unknown>,
  ctx: PoiPopupContext,
): MetaRow[] {
  const meta: MetaRow[] = [];
  const cat = ctx.category;
  const lang = ctx.lang;

  switch (cat) {
    case 'airports': {
      const iata = readStr(props, 'iata');
      const aType = readStr(props, 'aerodrome:type');
      if (iata) meta.push({ emoji: '🏷', text: `IATA: ${iata}` });
      if (aType) meta.push({ emoji: '✈', text: pickLang(AERODROME_LABELS[aType], lang, aType) });
      const ele = readNum(props, 'ele');
      if (ele != null && ele > 0) meta.push({ emoji: '⛰', text: PHRASES.altitude_m(Math.round(ele), lang) });
      const op = readStr(props, 'operator');
      if (op) meta.push({ emoji: '🏢', text: op });
      break;
    }
    case 'stations': {
      const rw = readStr(props, 'railway');
      if (rw) meta.push({ emoji: '🚆', text: pickLang(RAILWAY_LABELS[rw], lang, rw) });
      const network = readStr(props, 'network');
      const operator = readStr(props, 'operator');
      if (network) meta.push({ emoji: '🛤', text: network });
      else if (operator) meta.push({ emoji: '🛤', text: operator });
      break;
    }
    case 'hospitals': {
      const healthcare = readStr(props, 'healthcare');
      if (healthcare && healthcare !== 'hospital') meta.push({ emoji: '🏥', text: healthcare });
      if (readStr(props, 'emergency') === 'yes') meta.push({ emoji: '🚑', text: pickLang(PHRASES.emergency_24_7, lang, '') });
      const beds = readNum(props, 'beds');
      if (beds != null) meta.push({ emoji: '🛏', text: PHRASES.beds(beds, lang) });
      const spec = readStr(props, 'healthcare:speciality');
      if (spec) meta.push({ emoji: '🩺', text: spec.replace(/_/g, ' ') });
      const opType = readStr(props, 'operator:type');
      if (opType) meta.push({ emoji: '🏢', text: pickLang(OPERATOR_TYPE_LABELS[opType], lang, opType) });
      meta.push(...commonMeta(props));
      break;
    }
    case 'pharmacies': {
      meta.push(...commonMeta(props));
      if (readStr(props, 'dispensing') === 'yes') {
        meta.push({ emoji: '💊', text: pickLang(PHRASES.dispenses_rx, lang, '') });
      }
      break;
    }
    case 'supermarkets': {
      const brand = readStr(props, 'brand');
      if (brand) meta.push({ emoji: '🏷', text: brand });
      const shop = readStr(props, 'shop');
      if (shop === 'hypermarket') meta.push({ emoji: '🛒', text: pickLang(PHRASES.hypermarket, lang, '') });
      meta.push(...commonMeta(props));
      break;
    }
    case 'schools':
    case 'kindergartens': {
      const isced = readStr(props, 'isced:level');
      if (isced) {
        const lvl = isced.split(/[;,]/)[0].trim();
        const iscedLabel = ISCED_LABELS[lvl];
        if (iscedLabel) meta.push({ emoji: '🎓', text: pickLang(iscedLabel, lang, '') });
      }
      const stype = readStr(props, 'school:type');
      if (stype) meta.push({ emoji: '🏫', text: stype });
      const opType = readStr(props, 'operator:type');
      if (opType) meta.push({ emoji: '🏢', text: pickLang(OPERATOR_TYPE_LABELS[opType], lang, opType) });
      const language = readStr(props, 'language');
      if (language) meta.push({ emoji: '🗣', text: language });
      if (cat === 'kindergartens') {
        const minAge = readNum(props, 'min_age');
        const maxAge = readNum(props, 'max_age');
        if (minAge != null || maxAge != null) {
          meta.push({ emoji: '🧒', text: PHRASES.age_range(minAge ?? '?', maxAge ?? '?', lang) });
        }
      }
      meta.push(...commonMeta(props));
      break;
    }
    case 'ski_areas': {
      const lifts = readNum(props, 'lifts_count');
      if (lifts != null) meta.push({ emoji: '🚠', text: PHRASES.lifts(lifts, lang) });
      const eMin = readNum(props, 'ele_min');
      const eMax = readNum(props, 'ele_max');
      if (eMin != null && eMax != null && eMin !== eMax) {
        meta.push({ emoji: '⛰', text: PHRASES.altitude_m(`${eMin}–${eMax}` as unknown as number, lang) });
      }
      const mix = props.aerialway_mix as Record<string, number> | undefined;
      if (mix && typeof mix === 'object') {
        const parts = Object.entries(mix)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([k, v]) => `${v} ${pickLang(AERIALWAY_LABELS[k], lang, k)}`);
        if (parts.length) meta.push({ emoji: '⛷', text: parts.join(' · ') });
      }
      const area = readNum(props, 'bbox_area_km2');
      if (area != null && area > 0.5) {
        meta.push({ emoji: '📐', text: PHRASES.km2(area, lang) });
      }
      const op = readStr(props, 'operator');
      if (op) meta.push({ emoji: '🏢', text: op });
      break;
    }
    case 'ski_resorts': {
      const aw = readStr(props, 'aerialway');
      if (aw) meta.push({ emoji: '⛷', text: pickLang(AERIALWAY_LABELS[aw], lang, aw) });
      const eTop = readNum(props, 'ele:top');
      const eBot = readNum(props, 'ele:bottom');
      const ele = readNum(props, 'ele');
      if (eTop != null && eBot != null) {
        meta.push({ emoji: '⛰', text: PHRASES.altitude_m(`${Math.round(eBot)}–${Math.round(eTop)}` as unknown as number, lang) });
      } else if (ele != null && ele > 0) {
        meta.push({ emoji: '⛰', text: PHRASES.altitude_m(Math.round(ele), lang) });
      }
      const len = readNum(props, 'aerialway:length');
      if (len != null) meta.push({ emoji: '📏', text: PHRASES.length_m(Math.round(len), lang) });
      const op = readStr(props, 'operator');
      if (op) meta.push({ emoji: '🏢', text: op });
      break;
    }
    case 'alpine_huts': {
      const ele = readNum(props, 'ele');
      if (ele != null && ele > 0) meta.push({ emoji: '⛰', text: PHRASES.altitude_m(Math.round(ele), lang) });
      const cap = readNum(props, 'capacity');
      if (cap != null) meta.push({ emoji: '🛏', text: PHRASES.beds_total(cap, lang) });
      const season = readStr(props, 'seasonal');
      if (season === 'yes') meta.push({ emoji: '☀', text: pickLang(PHRASES.seasonal, lang, '') });
      else if (season === 'no') meta.push({ emoji: '🗓', text: pickLang(PHRASES.year_round, lang, '') });
      const amenities: string[] = [];
      if (readStr(props, 'shower') === 'yes') amenities.push(pickLang(PHRASES.shower, lang, ''));
      if (readStr(props, 'internet_access') === 'yes' || readStr(props, 'internet_access') === 'wlan') amenities.push(pickLang(PHRASES.wifi, lang, 'Wi-Fi'));
      if (readStr(props, 'meals') === 'yes') amenities.push(pickLang(PHRASES.meals, lang, ''));
      if (amenities.length) meta.push({ emoji: '✨', text: amenities.join(' · ') });
      meta.push(...commonMeta(props));
      const op = readStr(props, 'operator');
      if (op) meta.push({ emoji: '🏢', text: op });
      break;
    }
    case 'coworking': {
      const internet = readStr(props, 'internet_access');
      if (internet === 'yes' || internet === 'wlan') meta.push({ emoji: '📶', text: 'Wi-Fi' });
      meta.push(...commonMeta(props));
      break;
    }
    case 'language_schools': {
      const lang = readStr(props, 'language');
      if (lang) meta.push({ emoji: '🗣', text: lang });
      meta.push(...commonMeta(props));
      break;
    }
    case 'yoga':
    case 'gym':
    case 'pools':
    case 'courts': {
      const brand = readStr(props, 'brand');
      if (brand) meta.push({ emoji: '🏷', text: brand });
      const op = readStr(props, 'operator');
      if (op && !brand) meta.push({ emoji: '🏢', text: op });
      meta.push(...commonMeta(props));
      break;
    }
    case 'weather_stations':
      // легаси формат — отдельно через weatherTip
      break;
  }

  // Адрес (если есть) и ближайший город — в конце для контекста.
  const address = readStr(props, 'address');
  if (address) meta.push({ emoji: '📮', text: address });
  const near = nearestCityMeta(props);
  if (near) meta.push(near);

  return meta;
}

function pluralRu(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return '';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'а';
  return 'ов';
}

function buildActions(
  props: Record<string, unknown>,
  ctx: PoiPopupContext,
  name: string,
  searchQuery: string,
): ActionLink[] {
  const actions: ActionLink[] = [];

  // 1) Реальный сайт POI — если есть в OSM
  const website = readStr(props, 'website');
  if (website) {
    actions.push({ href: website, label: domainOf(website), emoji: '📎' });
  }

  // 2) Wikipedia — если есть
  const wiki = readStr(props, 'wikipedia');
  if (wiki) {
    const url = wikipediaLink(wiki);
    if (url) actions.push({ href: url, label: 'Wikipedia', emoji: '📚' });
  }

  // 3) FlightRadar24 только для аэропортов
  if (ctx.category === 'airports') {
    actions.push({
      href: `https://www.flightradar24.com/${ctx.lat.toFixed(2)},${ctx.lon.toFixed(2)}/9`,
      label: 'FlightRadar24',
      emoji: '✈',
    });
  }

  // 4) Google Maps по координатам — всегда работает
  actions.push({
    href: googleMapsLink(name, ctx.lat, ctx.lon),
    label: 'Google Maps',
    emoji: '🗺',
  });

  // 5) OpenStreetMap по osm_id
  const osm = osmLink(props);
  if (osm) actions.push({ href: osm, label: 'OpenStreetMap', emoji: '🌐' });

  // 6) Google Search — только если НЕТ website (иначе дублирует усилия)
  if (!website) {
    actions.push({
      href: googleSearchLink(searchQuery),
      label: ctx.lang === 'ru' ? 'Поиск в Google' : 'Search Google',
      emoji: '🔎',
    });
  }

  return actions;
}

function makePopupFormatter(searchQuery: (name: string) => string) {
  return (props: Record<string, unknown>, ctx: PoiPopupContext): string => {
    const cfg = POI_CONFIG[ctx.category];
    const name = getName(props);
    const meta = buildMeta(props, ctx);
    const actions = buildActions(props, ctx, name, searchQuery(name));
    const website = readStr(props, 'website');
    const nameHref = website ?? googleMapsLink(name, ctx.lat, ctx.lon);
    // Localised label provided by PoiLayer (read from i18n `poi.category.<id>`);
    // falls back to the russian `cfg.label` only if the caller forgot to pass it.
    const label = ctx.categoryLabel ?? cfg.label;
    return renderPopup(name, nameHref, cfg.emoji, label, meta, actions);
  };
}

// Контекстные google-запросы (нужны только если у POI нет website).
const airportsPopup = makePopupFormatter((name) => `${name} airport`);
const stationsPopup = makePopupFormatter((name) => `${name} stazione treno orari`);
const skiAreasPopup = makePopupFormatter((name) => `${name} ski area piste mappa`);
const skiResortsPopup = makePopupFormatter((name) => `${name} impianto sci`);
const alpineHutsPopup = makePopupFormatter((name) => `${name} rifugio CAI prenotazione`);
const hospitalsPopup = makePopupFormatter((name) => `${name} ospedale ASL contatti`);
const schoolsPopup = makePopupFormatter((name) => `${name} scuola in chiaro`);
const kindergartensPopup = makePopupFormatter((name) => `${name} scuola infanzia`);
const languageSchoolsPopup = makePopupFormatter(
  (name) => `${name} scuola lingue italiano stranieri`,
);
const coworkingPopup = makePopupFormatter((name) => `${name} coworking`);
const yogaPopup = makePopupFormatter((name) => `${name} yoga studio orari`);
const gymPopup = makePopupFormatter((name) => `${name} palestra orari`);
const poolsPopup = makePopupFormatter((name) => `${name} piscina orari ingresso`);
const courtsPopup = makePopupFormatter((name) => `${name} prenotazione campo sportivo`);
const supermarketsPopup = makePopupFormatter((name) => `${name} orari apertura`);
const pharmaciesPopup = makePopupFormatter((name) => `${name} farmacia orari turno`);

// ─── Per-category clustering profiles ───────────────────────────────────────
//
// Логика: редкие/важные POI (аэропорты, рифуджи) — слабая кластеризация и рано
// «раскрываются». Плотные бытовые (аптеки, супермаркеты) — сильно кластеризуются
// и раскрываются поздно, иначе экран замусорен.
//
function clusterProfile(
  ranges: [zoomLE: number, radius: number][],
  disableAt: number,
): PoiClusterConfig {
  return {
    radiusByZoom: (zoom: number) => {
      for (const [zMax, r] of ranges) {
        if (zoom <= zMax) return r;
      }
      return ranges[ranges.length - 1][1];
    },
    disableAt,
  };
}

// Профили в порядке от «совсем не кластеризовать» до «жёстко кластеризовать».
const CLUSTER_LANDMARK = clusterProfile(
  [[7, 35], [9, 22], [10, 12]],
  10,
); // airports — каждый объект важен
const CLUSTER_RARE = clusterProfile(
  [[7, 40], [9, 28], [10, 18], [11, 10]],
  11,
); // hospitals, alpine_huts, pools, coworking, yoga
const CLUSTER_MEDIUM = clusterProfile(
  [[7, 55], [9, 40], [11, 25], [12, 15]],
  12,
); // ski_resorts, kindergartens, courts, language_schools
const CLUSTER_DENSE = clusterProfile(
  [[7, 75], [9, 55], [11, 38], [12, 22]],
  13,
); // stations, schools, gym
const CLUSTER_VERY_DENSE = clusterProfile(
  [[7, 90], [9, 70], [11, 50], [13, 30]],
  14,
); // pharmacies, supermarkets

// ─── Конфиг ─────────────────────────────────────────────────────────────────
export const POI_CONFIG: Record<PoiCategory, PoiCategoryConfig> = {
  // ─── Transport ────────────────────────────────────────────────────────────
  airports: {
    id: 'airports', group: 'transport', label: 'Аэропорты', emoji: '✈',
    file: 'airports.geojson', color: '#7b1fa2', radius: 6,
    tooltipFormatter: basicTip, popupFormatter: airportsPopup,
    cluster: CLUSTER_LANDMARK,
  },
  stations: {
    id: 'stations', group: 'transport', label: 'ЖД-станции', emoji: '🚆',
    file: 'stations.geojson', color: '#43a047', radius: 3,
    tooltipFormatter: basicTip, popupFormatter: stationsPopup,
    cluster: CLUSTER_DENSE,
  },

  // ─── Essentials ───────────────────────────────────────────────────────────
  hospitals: {
    id: 'hospitals', group: 'essentials', label: 'Больницы', emoji: '🏥',
    file: 'hospitals.geojson', color: '#e53935', radius: 5,
    tooltipFormatter: basicTip, popupFormatter: hospitalsPopup,
    cluster: CLUSTER_RARE,
  },
  pharmacies: {
    id: 'pharmacies', group: 'essentials', label: 'Аптеки', emoji: '💊',
    file: 'pharmacies.geojson', color: '#43a047', radius: 3,
    tooltipFormatter: basicTip, popupFormatter: pharmaciesPopup,
    cluster: CLUSTER_VERY_DENSE,
  },
  supermarkets: {
    id: 'supermarkets', group: 'essentials', label: 'Супермаркеты', emoji: '🛒',
    file: 'supermarkets.geojson', color: '#fb8c00', radius: 3,
    tooltipFormatter: basicTip, popupFormatter: supermarketsPopup,
    cluster: CLUSTER_VERY_DENSE,
  },

  // ─── Спорт и отдых ────────────────────────────────────────────────────────
  yoga: {
    id: 'yoga', group: 'sports', label: 'Йога-студии', emoji: '🧘',
    file: 'yoga.geojson', color: '#8e24aa', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: yogaPopup,
    cluster: CLUSTER_RARE,
  },
  gym: {
    id: 'gym', group: 'sports', label: 'Тренажёрки / спорт-центры', emoji: '🏋',
    file: 'gym.geojson', color: '#00897b', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: gymPopup,
    cluster: CLUSTER_DENSE,
  },
  courts: {
    id: 'courts', group: 'sports', label: 'Спортплощадки (теннис/паддл/мульти)', emoji: '🏟',
    file: 'courts.geojson', color: '#558b2f', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: courtsPopup,
    cluster: CLUSTER_MEDIUM,
  },
  pools: {
    id: 'pools', group: 'sports', label: 'Бассейны', emoji: '🏊',
    file: 'pools.geojson', color: '#0288d1', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: poolsPopup,
    cluster: CLUSTER_RARE,
  },
  ski_areas: {
    id: 'ski_areas', group: 'sports', label: 'Горнолыжные курорты', emoji: '🏔',
    file: 'ski_areas.geojson', color: '#01579b', radius: 6,
    tooltipFormatter: basicTip, popupFormatter: skiAreasPopup,
    cluster: CLUSTER_LANDMARK,
  },
  ski_resorts: {
    id: 'ski_resorts', group: 'sports', label: 'Подъёмники', emoji: '⛷',
    file: 'ski_resorts.geojson', color: '#0277bd', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: skiResortsPopup,
    cluster: CLUSTER_MEDIUM,
  },
  alpine_huts: {
    id: 'alpine_huts', group: 'sports', label: 'Рифуджи', emoji: '🏔',
    file: 'alpine_huts.geojson', color: '#6d4c41', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: alpineHutsPopup,
    cluster: CLUSTER_RARE,
  },

  // ─── Дети ─────────────────────────────────────────────────────────────────
  kindergartens: {
    id: 'kindergartens', group: 'kids', label: 'Детсады', emoji: '🧸',
    file: 'kindergartens.geojson', color: '#f48fb1', radius: 3,
    tooltipFormatter: basicTip, popupFormatter: kindergartensPopup,
    cluster: CLUSTER_MEDIUM,
  },
  schools: {
    id: 'schools', group: 'kids', label: 'Школы', emoji: '🎓',
    file: 'schools.geojson', color: '#1e88e5', radius: 3,
    tooltipFormatter: basicTip, popupFormatter: schoolsPopup,
    cluster: CLUSTER_DENSE,
  },

  // ─── Взрослые ─────────────────────────────────────────────────────────────
  language_schools: {
    id: 'language_schools', group: 'adults', label: 'Языковые школы', emoji: '🗣',
    file: 'language_schools.geojson', color: '#5e35b1', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: languageSchoolsPopup,
    cluster: CLUSTER_MEDIUM,
  },
  coworking: {
    id: 'coworking', group: 'adults', label: 'Коворкинги', emoji: '💼',
    file: 'coworking.geojson', color: '#3949ab', radius: 4,
    tooltipFormatter: basicTip, popupFormatter: coworkingPopup,
    cluster: CLUSTER_RARE,
  },

  // ─── Скрыто (источник для калибровки климата, не UI-фильтр) ───────────────
  weather_stations: {
    id: 'weather_stations', group: 'essentials', label: 'Метеостанции', emoji: '🌡',
    file: 'weather_stations.geojson', color: '#fb8c00', radius: 5,
    tooltipFormatter: weatherTip,
    cluster: CLUSTER_RARE,
  },
};

export const GROUP_LABELS: Record<PoiGroup, string> = {
  transport: 'Транспорт',
  essentials: 'Ежедневное',
  sports: 'Спорт и отдых',
  kids: 'Дети',
  adults: 'Взрослые',
};

export const POI_GROUPS_ORDER: PoiGroup[] = ['transport', 'essentials', 'sports', 'kids', 'adults'];

export const POI_BY_GROUP: Record<PoiGroup, PoiCategory[]> = {
  transport: ['airports', 'stations'],
  essentials: ['hospitals', 'pharmacies', 'supermarkets'],
  sports: ['ski_areas', 'ski_resorts', 'alpine_huts', 'yoga', 'gym', 'courts', 'pools'],
  kids: ['kindergartens', 'schools'],
  adults: ['language_schools', 'coworking'],
};

export const POI_VISIBLE: PoiCategory[] = POI_GROUPS_ORDER.flatMap((g) => POI_BY_GROUP[g]);
